/**
 * FeeHierarchyTree.tsx
 * Simplified hierarchy rendering component using recursive structure
 */

import React, { useState } from 'react';
import { Plus, Minus, DollarSign, Edit, Trash2 } from 'lucide-react';
import { Card, CardBody, Button, Accordion, AccordionItem } from '@enfomentis/ui';
import type { TreeNode, DisplayFeeConfig } from '../hooks/useFeeHierarchy';
import type { AssetClassType, MarketSegment, SubAssetClass } from '@/types/shared/AssetClass';
import { ASSET_CLASS_TYPE_DISPLAY_MAP, MARKET_SEGMENT_DISPLAY_MAP, SUB_ASSET_CLASS_DISPLAY_MAP } from '@/constants/assetClass';
import { formatAssetClassTypeDisplay, formatMarketSegmentDisplay, formatSubAssetClassDisplay } from '@/utils/assetClass';

// Simplified props interface for the tree actions
interface HierarchyTreeActions {
  onAssignFees: (nodeId: string) => void;
  onEditFees: (nodeId: string) => void;
  onRemoveFees: (nodeId: string) => void;
  onRemoveLevel: (nodeId: string) => void;
  onAddSubLevel: (nodeId: string) => void;
  onAddInstrument: (nodeId: string) => void;
  onEditInstrumentFees: (instrumentId: string) => void;
  onRemoveInstrument: (instrumentId: string) => void;
  onAddAssetClass: () => void;
  onAddInstruments: () => void;
}

/**
 * Fee assignment card component
 */
interface FeeAssignmentCardProps {
  fees: DisplayFeeConfig;
  onEditFees: () => void;
  onRemoveFees: () => void;
}

const FeeAssignmentCard: React.FC<FeeAssignmentCardProps> = ({
  fees,
  onEditFees,
  onRemoveFees
}) => {
  return (
    <Card className="border border-default-200 mb-3" radius="sm" shadow="none">
      <CardBody className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="space-y-1">
              {fees.commission && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Commission:</span>
                  <span className="text-sm font-medium text-text-primary">{fees.commission}</span>
                </div>
              )}
              {fees.overnight && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Overnight:</span>
                  <span className="text-sm font-medium text-orange-600">{fees.overnight}</span>
                </div>
              )}
              {fees.conversion && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-text-secondary">Conversion:</span>
                  <span className="text-sm font-medium text-blue-600">{fees.conversion}</span>
                </div>
              )}
              {fees.notes && (
                <div className="mt-2 pt-2 border-t border-default-100">
                  <span className="text-sm text-text-secondary">Notes:</span>
                  <p className="text-sm text-text-primary mt-1">{fees.notes}</p>
                </div>
              )}
              {!fees.commission && !fees.overnight && !fees.conversion && (
                <div className="text-sm text-text-secondary italic">
                  No fees assigned
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 ml-3">
            <Button
              size="sm"
              variant="light"
              isIconOnly
              onPress={onEditFees}
              className="h-6 w-6"
            >
              <Edit className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="light"
              color="danger"
              isIconOnly
              onPress={onRemoveFees}
              className="h-6 w-6"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

/**
 * Instrument card component
 */
interface InstrumentFeeCardProps {
  symbol: string;
  fees: DisplayFeeConfig;
  onEditFees: () => void;
  onRemoveInstrument: () => void;
}

const InstrumentFeeCard: React.FC<InstrumentFeeCardProps> = ({
  symbol,
  fees,
  onEditFees,
  onRemoveInstrument
}) => {
  return (
    <Card className="border border-default-100 mb-2" radius="sm" shadow="none">
      <CardBody className="p-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h5 className="font-medium text-sm text-text-primary mb-2">{symbol}</h5>
            <div className="space-y-1">
              {fees.commission && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">Commission:</span>
                  <span className="text-xs font-medium text-text-primary">{fees.commission}</span>
                </div>
              )}
              {fees.overnight && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">Overnight:</span>
                  <span className="text-xs font-medium text-orange-600">{fees.overnight}</span>
                </div>
              )}
              {fees.conversion && (
                <div className="flex items-center justify-between">
                  <span className="text-xs text-text-secondary">Conversion:</span>
                  <span className="text-xs font-medium text-blue-600">{fees.conversion}</span>
                </div>
              )}
              {fees.notes && (
                <div className="mt-2 pt-2 border-t border-default-100">
                  <span className="text-xs text-text-secondary">Notes:</span>
                  <p className="text-xs text-text-primary mt-1">{fees.notes}</p>
                </div>
              )}
              {!fees.commission && !fees.overnight && !fees.conversion && (
                <div className="text-xs text-text-secondary italic">
                  No fees assigned
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 ml-3">
            <Button
              size="sm"
              variant="light"
              isIconOnly
              onPress={onEditFees}
              className="h-6 w-6"
            >
              <Edit className="w-3 h-3" />
            </Button>
            <Button
              size="sm"
              variant="light"
              color="danger"
              isIconOnly
              onPress={onRemoveInstrument}
              className="h-6 w-6"
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

// Props for the simplified tree component
interface FeeHierarchyTreeProps {
  hierarchy: TreeNode[];
  actions: HierarchyTreeActions;
}

export const FeeHierarchyTree: React.FC<FeeHierarchyTreeProps> = ({
  hierarchy,
  actions
}) => {
  // State for accordion expansion (multiple selection allowed) - shared across all levels
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

  // Generate all possible accordion keys from current hierarchy
  const currentKeys = React.useMemo(() => {
    const keys = new Set<string>();
    const generateKeys = (nodes: TreeNode[], parentPath: string = '') => {
      nodes.forEach(node => {
        const nodePath = parentPath ? `${parentPath} → ${node.name}` : node.name;
        keys.add(nodePath);
        generateKeys(node.children, nodePath);
      });
    };
    generateKeys(hierarchy);
    return keys;
  }, [hierarchy]);

  // Smart state preservation: only remove keys that no longer exist in hierarchy
  React.useEffect(() => {
    setExpandedKeys(prevKeys => {
      const validKeys = new Set<string>();
      prevKeys.forEach(key => {
        if (currentKeys.has(key)) {
          validKeys.add(key);
        }
      });
      return validKeys;
    });
  }, [currentKeys]);

  // Generate a dynamic key for accordion to force re-render when hierarchy changes
  const accordionKey = React.useMemo(() =>
    hierarchy.map(node => `${node.id}-${node.children.length}-${node.instruments.length}`).join('|'),
    [hierarchy]
  );

  // Empty state component
  const EmptyState: React.FC = () => (
    <div className="flex-1 flex items-center justify-center py-12">
      <div className="text-center max-w-md">
        <DollarSign className="w-16 h-16 text-text-secondary mx-auto mb-4" />
        <h4 className="text-lg font-medium text-text-primary mb-2">No Fee Configuration</h4>
        <p className="text-sm text-text-secondary">
          Start building your fee schedule by using the "Add Asset Class" or "Add Instruments" buttons above
        </p>
      </div>
    </div>
  );

  // If no hierarchy data, show empty state
  if (hierarchy.length === 0) {
    return <EmptyState />;
  }

  // Recursive rendering function - same pattern as the working version
  const renderHierarchyLevel = (
    node: TreeNode,
    fullPath: string,
    depth: number = 0
  ) => {
    const hasChildren = node.children.length > 0;
    const hasInstruments = node.instruments.length > 0;
    const hasContent = hasChildren || hasInstruments;

    const displayName = getDisplayName(node);
    const hasAssignedFees = node.fees && (
      node.fees.commission ||
      node.fees.overnight ||
      node.fees.conversion
    );

    return (
      <AccordionItem
        key={fullPath}
        textValue={`${displayName}${hasContent ? ` (${node.children.length + node.instruments.length} items)` : ''}`}
        indicator={
          expandedKeys.has(fullPath) ?
            <Minus className="w-4 h-4" /> :
            <Plus className="w-4 h-4" />
        }
        title={
          <div className="flex items-center justify-between w-full">
            <span className="font-medium">
              {displayName}
              {hasContent && (
                <span className="text-sm text-text-secondary ml-2">
                  ({node.children.length + node.instruments.length} items)
                </span>
              )}
            </span>
            <Trash2
              className="w-4 h-4 text-danger cursor-pointer hover:text-danger-600 transition-colors ml-2 mr-2"
              onClick={(e) => {
                e.stopPropagation();
                actions.onRemoveLevel(node.id);
              }}
            />
          </div>
        }
        classNames={{
          indicator: "order-first mr-2"
        }}
      >
        <div className="space-y-4">
          {/* Fee Assignment Card (if fees exist) */}
          {hasAssignedFees && node.fees && (
            <FeeAssignmentCard
              fees={node.fees}
              onEditFees={() => actions.onEditFees(node.id)}
              onRemoveFees={() => actions.onRemoveFees(node.id)}
            />
          )}

          {/* Action Buttons */}
          <div className="flex flex-row gap-2 justify-center items-center flex-wrap">
            {!hasAssignedFees && (
              <Button
                size="sm"
                variant="bordered"
                color="primary"
                startContent={<DollarSign className="w-3 h-3" />}
                onPress={() => actions.onAssignFees(node.id)}
                className="text-xs"
              >
                {getAssignFeesLabel(node)}
              </Button>
            )}
            {node.type !== 'sub-asset-class' && (
              <Button
                size="sm"
                variant="bordered"
                startContent={<Plus className="w-3 h-3" />}
                onPress={() => actions.onAddSubLevel(node.id)}
                className="text-xs"
              >
                {getNextLevelLabel(node)}
              </Button>
            )}
            <Button
              size="sm"
              variant="bordered"
              startContent={<Plus className="w-3 h-3" />}
              onPress={() => actions.onAddInstrument(node.id)}
              className="text-xs"
            >
              <span className="hidden sm:inline">Add </span>Instrument
            </Button>
          </div>

          {/* Sub-levels - NEW SEPARATE ACCORDION FOR CHILDREN */}
          {hasChildren && (
            <div className="space-y-2">
              <Accordion
                key={`${fullPath}-children-${node.children.length}`}
                variant="shadow"
                selectionMode="multiple"
                selectedKeys={expandedKeys}
                onSelectionChange={(keys) => setExpandedKeys(new Set(Array.from(keys) as string[]))}
                isCompact
                disableIndicatorAnimation
              >
                {node.children.map(childNode =>
                  renderHierarchyLevel(childNode, `${fullPath} → ${childNode.name}`, depth + 1)
                )}
              </Accordion>
            </div>
          )}

          {/* Direct Instruments */}
          {hasInstruments && (
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-text-primary">Individual Instruments</h4>
              <div className="space-y-2">
                {node.instruments.map((instrument) => (
                  <InstrumentFeeCard
                    key={instrument.id}
                    symbol={instrument.symbol}
                    fees={instrument.fees || { commission: null, overnight: null, conversion: null }}
                    onEditFees={() => actions.onEditInstrumentFees(instrument.id)}
                    onRemoveInstrument={() => actions.onRemoveInstrument(instrument.id)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </AccordionItem>
    );
  };

  // Helper functions for display names and labels
  const getDisplayName = (node: TreeNode): string => {
    switch (node.type) {
      case 'asset-class':
        return formatAssetClassTypeDisplay(node.name as AssetClassType, ASSET_CLASS_TYPE_DISPLAY_MAP);
      case 'market-segment':
        return formatMarketSegmentDisplay(node.name as MarketSegment, MARKET_SEGMENT_DISPLAY_MAP);
      case 'sub-asset-class':
        return formatSubAssetClassDisplay(node.name as SubAssetClass, SUB_ASSET_CLASS_DISPLAY_MAP);
      default:
        return node.name;
    }
  };

  const getNextLevelLabel = (node: TreeNode): React.ReactNode => {
    switch (node.type) {
      case 'asset-class': return (
        <>
          <span className="hidden sm:inline">Add </span>Market Segment
        </>
      );
      case 'market-segment': return (
        <>
          <span className="hidden sm:inline">Add </span>Sub-Asset Class
        </>
      );
      default: return (
        <>
          <span className="hidden sm:inline">Add </span>Sub-Level
        </>
      );
    }
  };

  const getAssignFeesLabel = (node: TreeNode): React.ReactNode => {
    switch (node.type) {
      case 'asset-class': return (
        <>
          <span className="hidden sm:inline">Assign </span>Asset Class<span className="hidden sm:inline"> Fees</span>
        </>
      );
      case 'market-segment': return (
        <>
          <span className="hidden sm:inline">Assign </span>Market Segment<span className="hidden sm:inline"> Fees</span>
        </>
      );
      case 'sub-asset-class': return (
        <>
          <span className="hidden sm:inline">Assign </span>Sub-Asset Class<span className="hidden sm:inline"> Fees</span>
        </>
      );
      default: return (
        <>
          <span className="hidden sm:inline">Assign </span>Fees
        </>
      );
    }
  };

  // Render the hierarchy tree using the working nested accordion pattern
  return (
    <Accordion
      key={accordionKey}
      variant="shadow"
      selectionMode="multiple"
      selectedKeys={expandedKeys}
      onSelectionChange={(keys) => setExpandedKeys(new Set(Array.from(keys) as string[]))}
      className="w-full"
      disableIndicatorAnimation
    >
      {hierarchy.map(node =>
        renderHierarchyLevel(node, node.name, 0)
      )}
    </Accordion>
  );
};